"use client";
import Header from "@/components/common/Header";
import React, { useState, useRef, useEffect } from "react";

const sections = [
    {
        id: "intro",
        title: "Introduction",
        content: [
            "Last updated: June 1, 2025.",
            "Welcome to VidhmSolutions.com (\"we\", \"us\", or \"our\"). We respect your privacy and are committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or engage our services. It aligns with applicable Indian laws including the Digital Personal Data Protection Act, 2023.",
        ],
    },

    {
        id: "what-we-collect",
        title: "1. Information We Collect",
        content: ["We may collect different types of information depending on your interaction with us:"],
        lists: [
            {
                heading: "Contact Information",
                items: [
                    "Name, email, phone number, business name, and address when you contact or register with us.",
                ],
            },
            {
                heading: "Project Information",
                items: [
                    "Software or service-related requirements, technical briefs, timelines, and business objectives.",
                ],
            },
            {
                heading: "Analytics & Usage Data",
                items: [
                    "IP address, device and browser type, pages visited, referral source, and session duration.",
                ],
            },
            {
                heading: "Communications",
                items: [
                    "Contact forms, emails, WhatsApp messages, proposals, or recorded calls (with consent).",
                ],
            },
            {
                heading: "Payment Information",
                items: ["Billing details for invoicing. We do NOT store bank or card details."],
            },
        ],
    },

    {
        id: "how-we-collect",
        title: "2. How We Collect Information",
        content: ["Information is collected in the following ways:"],
        lists: [
            { items: ["Directly from you — forms, calls, WhatsApp, proposals, invoices."] },
            { items: ["Automatically — cookies, analytics scripts, and log files."] },
            { items: ["From third-party sources — only when legally permitted and relevant."] },
        ],
    },

    {
        id: "how-we-use",
        title: "3. How We Use Your Information",
        content: ["Your personal data is used for purposes including, but not limited to:"],
        lists: [
            { items: ["Delivering requested IT, software, and digital services."] },
            { items: ["Providing support, updates, and communication regarding projects."] },
            { items: ["Improving services using analytics insights."] },
            { items: ["Sending promotional or marketing content (only with consent)."] },
            { items: ["Processing invoices and records."] },
            { items: ["Compliance with legal and regulatory requirements."] },
        ],
    },

    {
        id: "sharing",
        title: "4. How We Share Your Information",
        content: ["We do NOT sell your data. We may share information only when necessary:"],
        lists: [
            { items: ["Service providers: hosting, automation, analytics, and payment gateways."] },
            { items: ["Legal or regulatory compliance."] },
            { items: ["Business transfers (merger/acquisition)—with notice provided if applicable."] },
        ],
    },

    {
        id: "retention",
        title: "5. Data Retention",
        content: [
            "We keep data only as long as necessary for service fulfillment, legal compliance, or accounting. When no longer required, data is securely deleted or anonymized.",
        ],
    },

    {
        id: "security",
        title: "6. Data Security",
        content: [
            "We implement reasonable security measures to prevent unauthorized access. While we take precautions, no digital system can guarantee complete security.",
        ],
    },

    {
        id: "rights",
        title: "7. Your Rights",
        content: ["Subject to applicable laws, you may request:"],
        lists: [
            { items: ["Access to your data."] },
            { items: ["Correction of inaccurate data."] },
            { items: ["Deletion, where legally permitted."] },
            { items: ["Withdrawal of consent for marketing."] },
        ],
    },

    {
        id: "cookies",
        title: "8. Cookies & Tracking",
        content: [
            "We use cookies to improve user experience. You can control cookies through browser settings, but disabling them may affect functionality.",
        ],
    },

    {
        id: "third-party",
        title: "9. Third-Party Websites",
        content: [
            "We may link to external websites. Their privacy policies apply, not ours. Please review them before sharing your personal information.",
        ],
    },

    {
        id: "children",
        title: "10. Children’s Privacy",
        content: [
            "Our services are not intended for children under 18, and we do not knowingly collect their personal data.",
        ],
    },

    {
        id: "changes",
        title: "11. Policy Updates",
        content: [
            "We may revise this policy periodically. Any changes will be posted on this page with a revised date.",
        ],
    },

    {
        id: "contact",
        title: "12. Contact Us",
        content: [
            "For questions or privacy-related requests, contact:",
            "📧 support@vidhmsolutions.com",
            "🌐 www.vidhmsolutions.com",
        ],
    },
];

export default function PrivacyPolicyPage() {
    const [active, setActive] = useState(sections[0].id);
    const observer = useRef<IntersectionObserver | null>(null);

    useEffect(() => {
        const items = sections.map((s) => document.getElementById(s.id));
        observer.current = new IntersectionObserver(
            (entries) =>
                entries.forEach((entry) => entry.isIntersecting && setActive(entry.target.id)),
            { rootMargin: "-50% 0px -50% 0px" }
        );
        items.forEach((el) => el && observer.current?.observe(el));
        return () => observer.current?.disconnect();
    }, []);

    return (
        <div data-component-id="src/app/terms-of-services/page.tsx:174:8" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="174" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-gray-50%22%7D" className="min-h-screen bg-gray-50">
            <Header data-component-id="src/app/terms-of-services/page.tsx:175:12" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="175" data-component-file="page.tsx" data-component-name="Header" data-component-content="%7B%22elementName%22%3A%22Header%22%7D" />

            {/* Hero */}
            <section data-component-id="src/app/terms-of-services/page.tsx:178:12" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="178" data-component-file="page.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22bg-gradient-to-r%20from-indigo-600%20via-blue-600%20to-purple-600%20text-white%20py-48%20px-6%20text-center%22%7D" className="bg-gradient-to-r from-indigo-600 via-blue-600 to-purple-600 text-white py-48 px-6 text-center">
                <h1 data-component-id="src/app/terms-of-services/page.tsx:179:16" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="179" data-component-file="page.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-5xl%20font-extrabold%20text-white%22%2C%22textContent%22%3A%22Terms%20of%20Services%22%7D" className="text-5xl font-extrabold text-white">Terms of Services</h1>
                <p data-component-id="src/app/terms-of-services/page.tsx:180:16" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="180" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-4%20text-lg%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22We%20take%20your%20privacy%20seriously.%20Here's%20how%20we%20protect%20your%20information%20at%20VidhmSolutions.%22%7D" className="mt-4 text-lg max-w-2xl mx-auto">
                    We take your privacy seriously. Here's how we protect your information at VidhmSolutions.
                </p>
            </section>

            {/* Layout */}
            <div data-component-id="src/app/terms-of-services/page.tsx:186:12" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="186" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20grid%20grid-cols-1%20md%3Agrid-cols-4%20gap-8%20p-6%22%7D" className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8 p-6">

                {/* Sidebar */}
                <aside data-component-id="src/app/terms-of-services/page.tsx:189:16" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="189" data-component-file="page.tsx" data-component-name="aside" data-component-content="%7B%22elementName%22%3A%22aside%22%2C%22className%22%3A%22md%3Acol-span-1%22%7D" className="md:col-span-1">
                    <div data-component-id="src/app/terms-of-services/page.tsx:190:20" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="190" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22sticky%20top-10%20bg-white%20shadow-lg%20rounded-2xl%20p-6%20border%22%7D" className="sticky top-10 bg-white shadow-lg rounded-2xl p-6 border">
                        <h3 data-component-id="src/app/terms-of-services/page.tsx:191:24" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="191" data-component-file="page.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-bold%20text-gray-800%22%2C%22textContent%22%3A%22Contents%22%7D" className="font-bold text-gray-800">Contents</h3>
                        <nav data-component-id="src/app/terms-of-services/page.tsx:192:24" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="192" data-component-file="page.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22mt-4%20space-y-2%22%7D" className="mt-4 space-y-2">
                            {sections.map((s) => (
                                <button data-component-id="src/app/terms-of-services/page.tsx:194:32" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="194" data-component-file="page.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                                    key={s.id}
                                    onClick={() => {
                                        setActive(s.id);
                                        document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth" });
                                    }}
                                    className={`block w-full text-left text-sm py-2 px-3 rounded-md transition ${active === s.id ? "bg-indigo-100 text-indigo-700" : "hover:bg-gray-100 text-gray-600"
                                        }`}
                                >
                                    {s.title}
                                </button>
                            ))}
                        </nav>
                    </div>
                </aside>

                {/* Main Content */}
                <main data-component-id="src/app/terms-of-services/page.tsx:211:16" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="211" data-component-file="page.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22md%3Acol-span-3%20bg-white%20shadow-xl%20rounded-2xl%20p-10%20border%22%7D" className="md:col-span-3 bg-white shadow-xl rounded-2xl p-10 border">
                    {sections.map((section) => (
                        <section data-component-id="src/app/terms-of-services/page.tsx:213:24" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="213" data-component-file="page.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22mt-12%20first%3Amt-0%20scroll-mt-24%22%7D" key={section.id} id={section.id} className="mt-12 first:mt-0 scroll-mt-24">
                            <h2 data-component-id="src/app/terms-of-services/page.tsx:214:28" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="214" data-component-file="page.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20font-bold%20text-gray-900%22%7D" className="text-2xl font-bold text-gray-900">{section.title}</h2>

                            {section.content?.map((text, idx) => (
                                <p data-component-id="src/app/terms-of-services/page.tsx:217:32" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="217" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22mt-4%20text-gray-700%20leading-relaxed%22%7D" key={idx} className="mt-4 text-gray-700 leading-relaxed">
                                    {text}
                                </p>
                            ))}

                            {section.lists?.map((list, idx) => (
                                <div data-component-id="src/app/terms-of-services/page.tsx:223:32" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="223" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-4%22%7D" key={idx} className="mt-4">
                                    {list.heading && <h4 data-component-id="src/app/terms-of-services/page.tsx:224:53" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="224" data-component-file="page.tsx" data-component-name="h4" data-component-content="%7B%22elementName%22%3A%22h4%22%2C%22className%22%3A%22font-semibold%20text-gray-800%22%7D" className="font-semibold text-gray-800">{list.heading}</h4>}
                                    <ul data-component-id="src/app/terms-of-services/page.tsx:225:36" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="225" data-component-file="page.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22list-disc%20ml-6%20mt-2%20space-y-2%20text-gray-700%22%7D" className="list-disc ml-6 mt-2 space-y-2 text-gray-700">
                                        {list.items.map((item, i) => (
                                            <li data-component-id="src/app/terms-of-services/page.tsx:227:44" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="227" data-component-file="page.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%7D" key={i}>{item}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </section>
                    ))}

                    <footer data-component-id="src/app/terms-of-services/page.tsx:235:20" data-component-path="src/app/terms-of-services/page.tsx" data-component-line="235" data-component-file="page.tsx" data-component-name="footer" data-component-content="%7B%22elementName%22%3A%22footer%22%2C%22className%22%3A%22mt-16%20text-sm%20text-gray-600%20border-t%20pt-6%22%2C%22textContent%22%3A%22%C2%A9%20VidhmSolutions%20%E2%80%94%20All%20Rights%20Reserved.%22%7D" className="mt-16 text-sm text-gray-600 border-t pt-6">
                        © {new Date().getFullYear()} VidhmSolutions — All Rights Reserved.
                    </footer>
                </main>
            </div>
        </div>
    );
}
